import { useMemo, useState } from 'react'
import { api } from '../api.js'
import {
  Banner, Button, Card, ErrorState, Field, Loading, Metric, MetricRow, SectionHeader, StatusPill, Table,
} from '../components.jsx'
import { useApi } from '../hooks.js'
import PageHeader from './PageHeader.jsx'

/**
 * User accounts, native to the console.
 *
 * **This replaces a link, and the link was the defect.** The Setup page previously opened
 * `/admin/users.html` in a new tab — a separate page with its own login form and its own in-memory token, so
 * an administrator signed in again for every tool they touched. Reported directly: "why is it redirecting to
 * old UI and asking the same login multiple times for each workflow".
 *
 * I had deferred this on the grounds that the old pages work and field trials are close. That reasoning was
 * wrong in the same way the M14-03 deferral was: I judged the cost without measuring it. The API is complete
 * and already authenticated by the session this console holds — `GET/POST/PATCH/DELETE /admin/users`, plus
 * `reset-password` and `audit` — so porting it is a form against endpoints that already exist, not new
 * capability. Signing in six times to do one round of administration is not a rough edge to tidy later; it is
 * the thing that made the surface feel like a utility.
 *
 * Every role in the server's `Role` enum appears in the dropdowns, including MANAGEMENT. That set is pinned by
 * `AdminPageEnumIntegrationTest` for the old pages; the same completeness applies here, and it is the reason a
 * whole role was once uncreatable from the admin surface.
 */
export default function Users() {
  const list = useApi('/admin/users?size=200')
  const vendors = useApi('/admin/vendors')
  const audit = useApi('/admin/users/audit')

  const [q, setQ] = useState('')
  const [roleFilter, setRoleFilter] = useState('')
  const [editing, setEditing] = useState(null)     // a user row, or 'new'
  const [busy, setBusy] = useState(false)
  const [notice, setNotice] = useState(null)       // { kind, text }
  const [credential, setCredential] = useState(null) // shown once, never stored

  const page = list.data
  const staff = Array.isArray(page?.users) ? page.users : []
  const total = page?.totalUsers ?? staff.length

  const rows = useMemo(() => {
    let r = staff
    if (roleFilter) r = r.filter((u) => u.role === roleFilter)
    if (q.trim()) {
      const t = q.trim().toLowerCase()
      r = r.filter((u) => [u.staffNumber, u.name, u.designation, u.email, u.mobile, u.vendorName]
        .some((v) => v && String(v).toLowerCase().includes(t)))
    }
    return [...r].sort((a, b) => a.staffNumber.localeCompare(b.staffNumber))
  }, [staff, q, roleFilter])

  async function run(fn, successText) {
    setBusy(true)
    setNotice(null)
    try {
      const out = await fn()
      // Refresh from the server rather than mutating locally: the server owns `canSignIn`, timestamps and
      // vendor resolution, and guessing them here would put a row on screen that nothing agrees with.
      list.reload()
      audit.reload()
      setNotice({ kind: 'success', text: successText })
      return out
    } catch (e) {
      // The server's own message, which carries the real reason — a duplicate staff number, a vendor id
      // missing on a VENDOR account, a password under eight characters.
      setNotice({ kind: 'danger', text: e.message })
      return null
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader
        title="User accounts"
        subtitle="Create, correct, reset and deactivate. One sign-in — no separate login."
        actions={<Button onClick={() => { setEditing('new'); setCredential(null) }}>New account</Button>}
      />

      {notice && <Banner kind={notice.kind}>{notice.text}</Banner>}

      {/*
        A generated password is shown exactly once and never persisted. The server returns it on create and on
        reset; if it is lost, the remedy is another reset, not a lookup — which is the correct property for a
        credential and worth saying out loud on screen.
      */}
      {credential && (
        <Banner kind="warning" action={<Button variant="secondary" onClick={() => setCredential(null)}>Done</Button>}>
          <span>
            Password for <strong>{credential.staffNumber}</strong>:{' '}
            <code className="tnum">{credential.password}</code> — copy it now. It is not stored and cannot be
            shown again.
          </span>
        </Banner>
      )}

      {list.loading && <Loading rows={6} />}
      {list.error && <ErrorState error={list.error} onRetry={list.reload} />}

      {page && (
        <>
          <MetricRow>
            <Metric label="Accounts" value={total} onClick={() => setRoleFilter('')} />
            {ROLES.map((r) => (
              <Metric
                key={r.value}
                label={r.short}
                value={staff.filter((u) => u.role === r.value).length}
                onClick={() => setRoleFilter(r.value)}
              />
            ))}
            <Metric
              label="Deactivated"
              value={staff.filter((u) => u.active === false).length}
              hint="Cannot sign in"
              kind={staff.some((u) => u.active === false) ? 'warning' : undefined}
            />
          </MetricRow>

          <SectionHeader
            hint={roleFilter ? `filtered to ${roleFilter} · ${rows.length} of ${staff.length}` : `${staff.length} shown`}
          >
            Accounts
          </SectionHeader>

          <Card
            actions={
              <>
                <input
                  aria-label="Search accounts"
                  placeholder="Staff no, name, email…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  style={{ minWidth: 220 }}
                />
                <select aria-label="Role" value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)}>
                  <option value="">All roles</option>
                  {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </>
            }
          >
            <Table
              keyOf={(u) => u.id}
              columns={[
                { key: 'staffNumber', header: 'Staff no', render: (u) => <strong>{u.staffNumber}</strong> },
                {
                  key: 'name',
                  header: 'Name',
                  render: (u) => (
                    <span className="cell-stack">
                      <span>{u.name}</span>
                      {u.designation && <span className="muted">{u.designation}</span>}
                    </span>
                  ),
                },
                { key: 'role', header: 'Role', render: (u) => roleLabel(u.role) },
                { key: 'discipline', header: 'Discipline', render: (u) => u.discipline ? title(u.discipline) : <span className="muted">—</span> },
                {
                  key: 'contact',
                  header: 'Contact',
                  render: (u) => (
                    <span className="cell-stack">
                      {u.mobile && <span>{u.mobile}</span>}
                      {u.email && <span className="muted">{u.email}</span>}
                      {!u.mobile && !u.email && <span className="muted">—</span>}
                    </span>
                  ),
                },
                { key: 'vendor', header: 'Firm', render: (u) => u.vendorName || <span className="muted">—</span> },
                {
                  key: 'state',
                  header: 'State',
                  render: (u) => (
                    <span className="cell-row">
                      {u.active === false
                        ? <StatusPill status="CLOSED" title="Deactivated — cannot sign in" />
                        : <StatusPill status="ACTIVE" />}
                      {/*
                        `canSignIn` is distinct from `active`: a lightweight operator with no password is a
                        live account that cannot sign in. Showing both means an administrator can tell
                        "deactivated" from "never given a password", which one flag cannot express.
                      */}
                      {u.active !== false && u.canSignIn === false && (
                        <StatusPill status="PENDING_SYNC" title="No password set — cannot sign in yet" />
                      )}
                    </span>
                  ),
                },
                {
                  key: 'act',
                  header: '',
                  render: (u) => (
                    <span className="cell-row">
                      <Button variant="secondary" onClick={() => { setEditing(u); setCredential(null) }}>Edit</Button>
                    </span>
                  ),
                },
              ]}
              rows={rows}
              empty={<p className="muted">No account matches this view.</p>}
            />
          </Card>

          {audit.data && (
            <>
              <SectionHeader hint="Who changed which account, newest first">Account audit</SectionHeader>
              <Card>
                <Table
                  keyOf={(a) => a.id}
                  columns={[
                    { key: 'at', header: 'When', render: (a) => fmtDate(a.at || a.createdAt) },
                    { key: 'action', header: 'Action', render: (a) => title(a.action) },
                    { key: 'target', header: 'Account', render: (a) => a.targetStaffNumber || a.targetUserId || '—' },
                    { key: 'by', header: 'By', render: (a) => a.actorName || a.actorStaffNumber || '—' },
                    { key: 'detail', header: 'Detail', render: (a) => a.detail || <span className="muted">—</span> },
                  ]}
                  rows={Array.isArray(audit.data) ? audit.data : (audit.data.entries || [])}
                  empty={<p className="muted">No account changes recorded yet.</p>}
                />
                <p className="muted">
                  A password reset is recorded as having happened — never the password itself.
                </p>
              </Card>
            </>
          )}
        </>
      )}

      {editing && (
        <UserForm
          user={editing === 'new' ? null : editing}
          vendors={Array.isArray(vendors.data) ? vendors.data : []}
          busy={busy}
          onClose={() => setEditing(null)}
          onCreate={async (body) => {
            const out = await run(() => api('/admin/users', { method: 'POST', body }), `Created ${body.staffNumber}.`)
            if (out) {
              // The response is { user, generatedPassword } — the account is NESTED, not at the top level.
              // Verified by calling the endpoint rather than assuming, after exactly this class of guess blanked
              // the Setup page: `/admin/users` wraps its list under `users` and this wraps its row under `user`.
              const pw = out.generatedPassword || out.password
              if (pw) setCredential({ staffNumber: out.user?.staffNumber || body.staffNumber, password: pw })
              setEditing(null)
            }
          }}
          onSave={async (id, body) => {
            const ok = await run(() => api(`/admin/users/${id}`, { method: 'PATCH', body }), 'Account updated.')
            if (ok !== null) setEditing(null)
          }}
          onReset={async (id, staffNumber, newPassword) => {
            const out = await run(
              () => api(`/admin/users/${id}/reset-password`, { method: 'POST', body: newPassword ? { newPassword } : {} }),
              `Password reset for ${staffNumber}. Every session was revoked.`,
            )
            if (out) {
              const pw = out.generatedPassword || out.password || newPassword
              if (pw) setCredential({ staffNumber, password: pw })
              setEditing(null)
            }
          }}
          onDeactivate={async (id, staffNumber) => {
            const ok = await run(() => api(`/admin/users/${id}`, { method: 'DELETE' }), `${staffNumber} deactivated.`)
            if (ok !== null) setEditing(null)
          }}
        />
      )}
    </>
  )
}

/* ---- The create / edit sheet -------------------------------------------- */

function UserForm({ user, vendors, busy, onClose, onCreate, onSave, onReset, onDeactivate }) {
  const isNew = !user
  const [f, setF] = useState({
    staffNumber: user?.staffNumber || '',
    name: user?.name || '',
    role: user?.role || 'OPERATOR',
    discipline: user?.discipline || '',
    designation: user?.designation || '',
    mobile: user?.mobile || '',
    email: user?.email || '',
    vendorId: user?.vendorId || '',
    password: '',
  })
  const [resetPw, setResetPw] = useState('')
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value })

  // An engineer needs a discipline for the queue to scope to them; a vendor account needs a firm or the
  // server rejects it. Both mirror server-side validation rather than replacing it.
  const needsDiscipline = f.role === 'ELECTRICAL' || f.role === 'MECHANICAL'
  const needsVendor = f.role === 'VENDOR'
  const canSubmit = f.staffNumber.trim() && f.name.trim() && (!needsVendor || f.vendorId)

  function submit(e) {
    e.preventDefault()
    const body = {
      staffNumber: f.staffNumber.trim(),
      name: f.name.trim(),
      role: f.role,
      discipline: needsDiscipline ? (f.discipline || f.role) : null,
      designation: f.designation.trim() || null,
      mobile: f.mobile.trim() || null,
      email: f.email.trim() || null,
      vendorId: needsVendor ? f.vendorId : null,
    }
    if (isNew) {
      // Blank password is the recommended path: the server generates a strong one and returns it once, which
      // is better than an administrator inventing "welcome123" for a plant account.
      onCreate({ ...body, password: f.password.trim() || null })
    } else {
      const { staffNumber, ...patch } = body   // staff number is identity; changing it is not an edit
      onSave(user.id, patch)
    }
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <form className="sheet" onClick={(e) => e.stopPropagation()} onSubmit={submit}>
        <header className="sheet-head">
          <h2>{isNew ? 'New account' : `Edit ${user.staffNumber}`}</h2>
          <button type="button" className="btn" onClick={onClose}>Close</button>
        </header>

        <div className="sheet-body">
          <div className="grid-2">
            <Field label="Staff number" id="sn" hint={isNew ? 'How they sign in. Not an email address.' : 'Identity — cannot be changed'}>
              <input id="sn" value={f.staffNumber} onChange={set('staffNumber')} disabled={!isNew} required />
            </Field>
            <Field label="Full name" id="nm">
              <input id="nm" value={f.name} onChange={set('name')} required />
            </Field>
            <Field label="Role" id="rl">
              <select id="rl" value={f.role} onChange={set('role')}>
                {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </Field>
            {needsDiscipline && (
              <Field label="Discipline" id="dc" hint="Which queue this engineer sees">
                <select id="dc" value={f.discipline || f.role} onChange={set('discipline')}>
                  <option value="ELECTRICAL">Electrical</option>
                  <option value="MECHANICAL">Mechanical</option>
                  <option value="EITHER">Either</option>
                </select>
              </Field>
            )}
            {needsVendor && (
              <Field label="Firm" id="vd" hint="A vendor account must belong to a registered firm">
                <select id="vd" value={f.vendorId} onChange={set('vendorId')} required>
                  <option value="">Select…</option>
                  {vendors.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
                </select>
              </Field>
            )}
            <Field label="Designation" id="dg">
              <input id="dg" value={f.designation} onChange={set('designation')} placeholder="Crane Operator" />
            </Field>
            <Field label="Mobile" id="mb">
              <input id="mb" value={f.mobile} onChange={set('mobile')} inputMode="tel" />
            </Field>
            <Field label="Email" id="em">
              <input id="em" type="email" value={f.email} onChange={set('email')} />
            </Field>
            {isNew && (
              <Field label="Password" id="pw" hint="Leave blank and the server generates one, shown once">
                <input id="pw" type="text" value={f.password} onChange={set('password')} placeholder="(generate)" />
              </Field>
            )}
          </div>

          {f.role === 'MANAGEMENT' && (
            <Banner kind="info">
              A management account is <strong>read-only</strong>: fleet analytics and reports on this console,
              and no write access anywhere. It cannot be used on the Android app.
            </Banner>
          )}

          {!isNew && (
            <>
              <SectionHeader>Password</SectionHeader>
              <div className="row">
                <Field label="New password" id="rp" hint="Blank generates one. Every session is revoked either way.">
                  <input id="rp" type="text" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="(generate)" />
                </Field>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={busy}
                  onClick={() => onReset(user.id, user.staffNumber, resetPw.trim())}
                >
                  Reset password
                </Button>
              </div>

              {user.active !== false && (
                <>
                  <SectionHeader>Deactivate</SectionHeader>
                  <p className="muted">
                    Deactivating keeps the account and its history — every report they filed stays attributed —
                    and stops them signing in. Their refresh tokens stop working on the next attempt.
                  </p>
                  <Button
                    type="button"
                    variant="danger"
                    disabled={busy}
                    onClick={() => onDeactivate(user.id, user.staffNumber)}
                  >
                    Deactivate {user.staffNumber}
                  </Button>
                </>
              )}
            </>
          )}
        </div>

        <footer className="sheet-foot">
          <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
          <Button type="submit" disabled={busy || !canSubmit}>
            {busy ? 'Saving…' : isNew ? 'Create account' : 'Save changes'}
          </Button>
        </footer>
      </form>
    </div>
  )
}

/**
 * Every role the server accepts.
 *
 * The old pages once omitted VENDOR, which made a whole role uncreatable from the admin surface, and omitted
 * MANAGEMENT until this release — caught only because a test asserts these options equal the `Role` enum
 * exactly. Kept complete here for the same reason.
 */
const ROLES = [
  { value: 'OPERATOR', label: 'Operator', short: 'Operators' },
  { value: 'MECHANICAL', label: 'Engineer — mechanical', short: 'Mechanical' },
  { value: 'ELECTRICAL', label: 'Engineer — electrical', short: 'Electrical' },
  { value: 'SAFETY_OFFICER', label: 'Safety officer', short: 'Safety' },
  { value: 'ADMIN', label: 'Administrator', short: 'Admins' },
  { value: 'MANAGEMENT', label: 'Management — dashboard only', short: 'Management' },
  { value: 'VENDOR', label: 'Vendor', short: 'Vendors' },
]

const roleLabel = (r) => ROLES.find((x) => x.value === r)?.label || r
const title = (s) => (s ? s.split('_').map((w) => w.charAt(0) + w.slice(1).toLowerCase()).join(' ') : '—')

function fmtDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString(undefined, {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}
